import { useEffect, useState } from 'react';
import { type DistinctHierarchy } from '@sr/types';
import { type PartnerInfo } from 'core/lib/types';
import { type ApiHooks } from '../../types';
import useSubscriptionsPartners from './useSubscriptionPartners';

const useAvailableSubscriptionHierarchies = (apiHooks: ApiHooks) => {
  const { partners: validPartners } = useSubscriptionsPartners();
  const [hierarchies, setHierarchies] = useState<DistinctHierarchy[]>();
  const [loadingDistinctHierarchies, setLoadingDistinctHierarchies] = useState<boolean>(false);

  const [initialLoaded, setInitialLoaded] = useState<boolean>(false);

  const [hierarchyDetails, setHierarchyDetails] = useState<PartnerInfo[]>();
  const [loadingDetails, setLoadingDetails] = useState<boolean>(false);

  const { getDistinctHierarchies, getPartnerInfo } = apiHooks;

  useEffect(() => {
    if (!initialLoaded && !hierarchies && !loadingDistinctHierarchies && validPartners) {
      setInitialLoaded(true);
      setLoadingDistinctHierarchies(true);
      getDistinctHierarchies(validPartners)
        .then((hierarchies) => {
          setHierarchies(hierarchies.availableHierarchies);
        })
        .catch((err: unknown) => {
          console.error(err);
        })
        .finally(() => {
          setLoadingDistinctHierarchies(false);
        });
    }
  }, [
    validPartners,
    hierarchies,
    loadingDistinctHierarchies,
    getDistinctHierarchies,
    initialLoaded,
  ]);

  useEffect(() => {
    if (
      !loadingDetails &&
      !loadingDistinctHierarchies &&
      hierarchies?.length &&
      !hierarchyDetails
    ) {
      setLoadingDetails(true);
      getPartnerInfo(hierarchies.map(({ dwKeys }) => dwKeys[0]))
        .then((details) => {
          setHierarchyDetails(details);
        })
        .catch((err: unknown) => {
          console.error(err);
        })
        .finally(() => {
          setLoadingDetails(false);
        });
    }
  }, [loadingDetails, hierarchies, loadingDistinctHierarchies, hierarchyDetails, getPartnerInfo]);

  return {
    hierarchies,
    loading: loadingDistinctHierarchies || loadingDetails,
    details: hierarchyDetails,
  };
};

export default useAvailableSubscriptionHierarchies;
